import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { decimalHours, formatMinutes, localDateOf } from '@kronos/domain'
import type { Instant, LocalDate, PersonHours } from '@kronos/domain'
import { repository } from '../../data'
import { EmptyState, Panel, StatTile } from '../primitives'
import { DayCard } from './DayCard'
import { PeriodNav } from './PeriodNav'
import { ShiftEditor } from './ShiftEditor'
import type { ShiftEdit } from './ShiftEditor'
import { problemOf } from './time'

/**
 * Everyone's hours for a pay period, with the PDF whoever pays reads a click
 * away. An engineer can fix anyone's times from here; the report still marks
 * them as typed in, whoever typed them.
 */
export function OfficeHoursView(): JSX.Element {
  const [date, setDate] = useState<LocalDate | null>(null)
  const [editing, setEditing] = useState<ShiftEdit | null>(null)
  const hours = useQuery({ queryKey: ['hours', date], queryFn: () => repository.hours(date ?? undefined) })
  // The period that contains today, to stop ‹ › walking into the future.
  const today = localDateOf(new Date().toISOString() as Instant, hours.data?.timeZone ?? 'UTC')

  if (hours.isPending) return <p className="muted">Loading…</p>
  if (hours.isError) return <p className="critical">{problemOf(hours.error)}</p>
  const report = hours.data

  return (
    <div className="page">
      <div className="toolbar">
        <PeriodNav period={report.period} today={today} onChange={setDate} />
        <span className="toolbar__spacer" />
        <a className="button" href={repository.hoursPdfHref(date ?? undefined)}>
          Download PDF
        </a>
      </div>

      <div className="stat-tiles">
        <StatTile label="Total" value={formatMinutes(report.minutes)} sub={`${decimalHours(report.minutes)} decimal hours`} />
        <StatTile label="Technicians" value={report.people.length} />
        <StatTile label="Needs a look" value={report.needsALook} warning={report.needsALook > 0} />
      </div>

      {report.people.length === 0 ? (
        <Panel>
          <EmptyState icon="office" title="No technicians yet">
            Give someone the technician role under Team, and their Today starts with a clock.
          </EmptyState>
        </Panel>
      ) : null}

      {report.people.map((p) => (
        <Person key={p.userId} person={p} onEdit={setEditing} />
      ))}

      <p className="muted" style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        Kronos keeps hours only — no pay rates. The PDF is what whoever pays reads.
      </p>

      {editing ? <ShiftEditor edit={editing} onClose={() => setEditing(null)} /> : null}
    </div>
  )
}

function Person({ person, onEdit }: { person: PersonHours; onEdit: (e: ShiftEdit) => void }): JSX.Element {
  return (
    <section className="section">
      <div className="person-head">
        <span className="stack" style={{ gap: 0, minWidth: 0 }}>
          <strong>{person.name}</strong>
          <span className="muted" style={{ fontSize: 'var(--font-size-sm)' }}>
            {person.email}
          </span>
        </span>
        <span className="person-head__total">
          <span className="figure">{formatMinutes(person.minutes)}</span>
          <span className="muted" style={{ fontSize: 'var(--font-size-sm)' }}>
            {decimalHours(person.minutes)} h
          </span>
        </span>
      </div>
      <Panel>
        {person.days.length === 0 ? (
          <EmptyState title="No shifts this period" />
        ) : (
          person.days.map((d) => (
            <DayCard
              key={d.date}
              day={d}
              who={person.name}
              onEdit={(e) => onEdit(e.kind === 'add' ? { ...e, userId: person.userId } : e)}
            />
          ))
        )}
      </Panel>
    </section>
  )
}
